import { IEntity, IEquatable } from '../types';
import { isEntity } from './typeGuards';

/**
 * Converts a value to an `IEquatable` or `undefined`.
 * If the value is an `IEntity`, it extracts the `id` property.
 * If the `id` is a string that represents a valid number, it returns the number.
 * Otherwise, it returns the `id` as is.
 *
 * @param value The value to convert.
 * @returns The converted value as an `IEquatable` or `undefined`.
 */
export function asEquatable(value: string): IEquatable;
export function asEquatable(value: number): number;
export function asEquatable(value: IEntity | IEquatable | undefined): IEquatable | undefined {
  const id = isEntity(value) ? value.id : value;
  if (typeof id === 'string') {
    if (String(parseInt(id)) === id) {
      return Number(id);
    }
  }
  return id;
}

/**
 * Converts a value to a string or `undefined`.
 * If the value is an `IEntity`, it extracts the `id` property.
 * If the `id` is defined, it returns it as a string.
 * Otherwise, it returns `undefined`.
 *
 * @param value The value to convert.
 * @returns The converted value as a string or `undefined`.
 */
export function asEquatableString(value: string): string;
export function asEquatableString(value?: IEntity | IEquatable): string | undefined {
  const id = isEntity(value) ? value.id : value;
  if (typeof id !== 'undefined') {
    return String(id);
  }
  return id;
}

/**
 * Converts a value to a number or `undefined`.
 * If the value is an `IEntity`, it extracts the `id` property.
 * If the `id` is a string that represents a valid number, it returns the number.
 * Otherwise, it returns `undefined`.
 *
 * @param value The value to convert.
 * @returns The converted value as a number or `undefined`.
 */
export function asEquatableNumber(value: number): number;
export function asEquatableNumber(value?: IEntity | IEquatable): number | undefined {
  const id = isEntity(value) ? value.id : value;
  if (typeof id === 'string') {
    if (String(parseInt(id)) === id) {
      return Number(id);
    } else {
      return undefined;
    }
  }
  return id;
}
